import Platform from 'quasar/src/plugins/Platform.js';import Screen from 'quasar/src/plugins/Screen.js';
import {defineStore} from 'pinia'
import {IOptionList, IRadioGroupOption, IStringIndexedArrayOfBoolean} from "@/interface"
import {ContactSubjectEnum, InvoiceStepFilterEnum, LanguageEnum, RadioGroupEnum} from '@/enums'

import i18n from "@/locales"

export const useCommonStore = defineStore('common', {
    state: (): ICommonState => ({
        temporaryUser: null,
        forceContractId: null,
        showHelp: false,
        showLogout: false,
        showMenu: false,
        globalLoading: false,
        layoutLoading: false,
        spinnerVisible: true,
        internetOnline: true,
        requestLoading: false,
        currentLoading: {},
        contactSubjects: Object.values(ContactSubjectEnum),
        languages: Object.values(LanguageEnum),
        filterUsageOptions: [
            { label: 'consumption.filterOptions.currentBill', value: InvoiceStepFilterEnum.CURRENT_INVOICE, sideType: RadioGroupEnum.None },
            { label: 'consumption.filterOptions.previousBill', value: InvoiceStepFilterEnum.PREVIOUS_INVOICE, sideType: RadioGroupEnum.None },
            { label: 'consumption.filterOptions.selectPeriod', value: InvoiceStepFilterEnum.DATES_INVOICE, sideType: RadioGroupEnum.None },
        ] as IRadioGroupOption[]
    }),
    actions: {
        clearAllModals() {
            this.showHelp = false;
            this.showMenu = false;
            this.showLogout = false;
        },
        findLanguageLabel(value: string) { 
            return this.getTranslatedLanguages.find(e => e.value === value)?.label ?? ''
        },
        isLoadingState(part: string|string[]) { 
            if (Array.isArray(part)) {
                for (const i in part) {
                    if (this.currentLoading[part[i]]) {
                        return true
                    }
                }
                return false
            } else {
                return this.currentLoading[part] ?? false
            }
        },
        setForceContractId(val: number|null) {
            this.forceContractId = val
        },
        setTemporaryUser(valFirstName: string|null, valLastName: string) {
            this.temporaryUser = {
                firstName: valFirstName, 
                lastName: valLastName
            }
        },
        startLoading(global: boolean, spinnerVisible: boolean) {
            if (global) {
                this.globalLoading = true
            } else {
                this.layoutLoading = true
            }

            this.spinnerVisible = spinnerVisible
        },
        setLoading(part: string|string[], loading = true) {
            if (Array.isArray(part)) {
                for (const i in part) {
                    this.currentLoading[part[i]] = loading
                }
            } else {
                this.currentLoading[part] = loading
            }
        },
        stopLoading() {
            this.spinnerVisible = true 
            this.globalLoading = false 
            this.layoutLoading = false
        },
    },
    getters: {
        firstLastName(_state: ICommonState) : string|null {
            let value = ''
            if (this.temporaryUser) {
                value = (this.temporaryUser.firstName ? this.temporaryUser.firstName + ' ' : '') + (this.temporaryUser.lastName ?? '')
            }

            return (value == '' ? null : value)
        },
        isScreenWidthSmall(_state: ICommonState) : boolean {
            return Screen.width >= 600 && Screen.width <= 1023.99
        },
        isScreenWidthLarge(_state: ICommonState) : boolean {
            return Screen.width <= 1199.99
        },
        isMobile(_state: ICommonState) : boolean {
            return Platform.is.mobile || Screen.width < 800
        },
        isApple() : boolean {
            return !!Platform.is.iphone || !!Platform.is.ipad || !!Platform.is.ios;
        },
        isAndroid() : boolean {
            return !!Platform.is.android;
        },
        isIos() : boolean {
            return !!Platform.is.ios;
        },
        isLoading(state: ICommonState) : boolean {
            return state.globalLoading || state.layoutLoading
        },
        isLoadingCustomer(state: ICommonState) : boolean {
            return state.currentLoading["customer"] ?? false
        },
        getContactOptions(state: ICommonState) : Array<IOptionList> {
            const newArray = [] as Array<IOptionList>

            state.contactSubjects.forEach(e => {
                newArray.push({ label: i18n.global.t(`common.contacts.subject.${e}`), value: e})
            })

            return newArray
        },
        getFilterUsageOptions(state: ICommonState) : Array<IRadioGroupOption> {
            const newArray = [] as Array<IRadioGroupOption>

            state.filterUsageOptions.forEach(element => {
                newArray.push({ label: i18n.global.t(element.label), value: element.value, sideType: element.sideType })
            })

            return newArray
        },
        getTranslatedLanguages(state: ICommonState): Array<IOptionList> {
            const newArray = [] as Array<IOptionList>
            
            state.languages.forEach(e => {
                newArray.push({ label: i18n.global.t(`common.langs.${e}`), value: e})
            })

            return newArray
        },
        loading(state: ICommonState): boolean {
            return state.layoutLoading || state.globalLoading
        }
    }
})

interface ICommonState {
    showHelp: boolean,
    showLogout: boolean,
    showMenu: boolean,
    globalLoading: boolean,
    layoutLoading: boolean,
    spinnerVisible: boolean,
    requestLoading: boolean,
    currentLoading: IStringIndexedArrayOfBoolean,
    internetOnline: boolean,
    contactSubjects: string[],
    languages: string[],
    filterUsageOptions: Array<IRadioGroupOption>,
    forceContractId: number|null,
    temporaryUser: {
        firstName: string|null,
        lastName: string
    } | null,
}

export type CommonStore = ReturnType<typeof useCommonStore>